import { GlobalStateController } from "bai-react-global-state";
import firebase from "firebase/compat/app";
import React, { Fragment } from "react";

import { presentations as presentationsApi } from "apis/callables";
import { AppView } from "common/constants";
import { IPresentation, IStorageModel, IUserStorageModel, IWorkspaceMetadata } from "common/interfaces";
import { isOwnerOrLibrarian } from "common/utils/roles";
import * as analytics from "js/analytics";
import PresentationLibraryController, { PresentationLibraryFilter, PresentationFilterType } from "js/controllers/PresentationLibraryController";
import WorkspaceController, { getInitialWorkspaceId } from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Presentation } from "js/core/models/presentation";
import { User } from "js/core/models/user";
import NotificationsService from "js/core/services/notifications";
import isConnected from "js/core/utilities/isConnected";
import { trackActivity } from "js/core/utilities/utilities";
import { initializeForceReloadDialog } from "js/editor/forceReload";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { app } from "js/namespaces";
import { ShowDialog, ShowDialogAsync, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import UpdateAppDialog from "js/react/components/Dialogs/UpdateAppDialog";
import { ShowNotificationAlert } from "js/react/components/NotificationAlert";
import { browserHistory } from "js/react/history";
import { ForcedDowngradeDialog } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import MemberAddedToOrgDialog from "js/react/views/UserOptions/dialogs/MemberAddedToOrgDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.CONTROLLERS);

// Preloads needed for the editor
import "js/editor/preload";

declare global {
    interface Window {
        roomID: string;
        Appcues: any;
        baiSource: string;
    }
}

export interface AppControllerState {
    isConnected: boolean;
    isConstrained: boolean;
    view: typeof AppView[keyof typeof AppView];
    externalActivePanel: string;
    user: IUserStorageModel;
    userAvatarUrl: string;
    isAccountVerified: boolean;
    workspaceId: string;
    firebaseUser: firebase.User;
}

const initialState: AppControllerState = {
    // App is running in a iframe
    isConstrained: false,
    // Browser is connected to the internet
    isConnected: true,
    workspaceId: null,
    userAvatarUrl: null,
    view: null,
    user: null,
    isAccountVerified: true,
    externalActivePanel: null,
    firebaseUser: null
};

class AppController extends GlobalStateController<AppControllerState> {
    private isSubscribedToConnectionChange: boolean = false;
    public notificationsService: NotificationsService = null;
    public initialized: boolean = false;

    get orgId() {
        return (this._state.workspaceId === "personal") ? null : this._state.workspaceId;
    }

    get workspaceId() {
        return this._state.workspaceId;
    }

    get view() {
        return this._state.view;
    }

    get externalActivePanel() {
        return this._state.externalActivePanel;
    }

    get currentTeam() {
        if (!this.orgId) {
            return null;
        }

        return ds.teams.defaultTeamForOrg(this.orgId);
    }

    get userAvatarUrl() {
        return this._state.userAvatarUrl;
    }

    get ds() {
        return ds;
    }

    get firebaseUser() {
        return this._state.firebaseUser;
    }

    get user() {
        return this._state.user;
    }

    get workspaceIsPastDue() {
        return WorkspaceController.isPastDue;
    }

    get workspacesMetadata() {
        return (this._state.user?.workspacesMetadata ?? []) as IWorkspaceMetadata[];
    }

    initializePromise = Promise.resolve();

    initialize(firebaseUser, initialWorkspaceId?: string) {
        return new Promise((resolve, reject) => {
            this.initializePromise = this.initializePromise
                .then(async () => {
                    if (!window.roomID) {
                        window.roomID = new URLSearchParams(window.location.search).get("roomID");
                    }

                    // Force set initial state but keep the view
                    await this._updateState(() => ({ ...initialState, view: this.view }));

                    await this.initializeUser(firebaseUser);

                    await this.setInitialWorkspaceId(initialWorkspaceId);

                    await analytics.identify(this.user.getAuthUser());

                    await this._updateState({
                        isConstrained: app.isConstrained,
                        isConnected: isConnected.connected,
                        userAvatarUrl: this.user.getAvatarUrl(),
                    });

                    this.initializeNotificationsService();

                    // poll for forced reload
                    initializeForceReloadDialog();

                    if (!this.isSubscribedToConnectionChange) {
                        this.isSubscribedToConnectionChange = true;

                        // update the state if we're offline
                        isConnected.on("change", () => {
                            this._updateState({ isConnected: isConnected.connected });
                        });
                    }

                    // if user came from signup, signup is now complete,
                    // but first wait for analytics events to fire
                    setTimeout(() => {
                        if (analytics.getState("is_signing_up")) {
                            analytics.trackState({
                                is_signing_up: false
                            });
                        }
                    }, 2000);

                    this.initialized = true;
                })
                .then(resolve)
                .catch(reject);
        });
    }

    async reset() {
        logger.info("[AppController] reset");
        this.initialized = false;

        if (this.notificationsService) {
            this.notificationsService.dispose();
        }

        if (this.user) {
            this.user.off();
        }
        if (this.isSubscribedToConnectionChange) {
            isConnected.off("change");
            this.isSubscribedToConnectionChange = false;
        }

        PresentationLibraryController.reset();
        // Force set initial state
        await this._updateState(() => ({ ...initialState }));
    }

    setExternalActivePanel(panel: string) {
        // De-select any selected element when openinng a panel
        if (panel) ds.selection.elment = null;
        this._updateState({ externalActivePanel: panel });
    }

    async setView(view: typeof AppView[keyof typeof AppView]) {
        await this.initializePromise;

        await this._updateState({ view });

        await this._handleTour();
    }

    private async _handleTour() {
        if (!this.initialized) {
            return;
        }

        const { view, workspaceId } = this._state;

        const isNonMigrated = this.user.get("_migrated") === false;
        if (
            isNonMigrated &&
            view === AppView.LIBRARY
        ) {
            this.user.update({ _migrated: null });

            ShowDialog(UpdateAppDialog, {
                title: "Welcome to a new version of Beautiful.ai!",
                message: "We’ve made the editing experience more powerful, flexible and easier to navigate.",
                submessage: "You can upgrade to the new version of Beautiful.ai by clicking the button below.",
                secondButtonLabel: "Take the tour",
                onFirstButtonClick: () => {
                    window.open("https://support.beautiful.ai/hc/en-us/articles/31146296163597", "_blank");
                },
                onSecondButtonClick: () => {
                    window.open("https://www.beautiful.ai/copydeck/-O9upFY5ZT44CvkaO4WK/1?appcue=536961b7-0cbc-4c5f-8b7a-e40bd4d5f416", "_self");
                }
            });
        }

        if (workspaceId !== "personal") {
            // Not in personal workspace, tours are only shown in personal workspace
            return;
        }

        if (window.baiSource === "collab:view" || window.baiSource === "secureshare") {
            // Landed on library from collab view invite or a secure share
            return;
        }

        const hasTakenTour = !!this.user.get("hasTakenTour");
        if (hasTakenTour) {
            // Already has taken tour
            return;
        }

        const setHasTakenTour = async () => {
            this.user.update({ hasTakenTour: true });
            await this.user.updatePromise;
        };

        const urlParams = new URLSearchParams(window.location.search);
        const appcueFlowId = urlParams.get("with-appcue");
        if (appcueFlowId) {
            await setHasTakenTour();

            // Specific appcue flow requested
            window.Appcues.show(appcueFlowId);
            return;
        }

        if (view !== AppView.LIBRARY) {
            // The following tours are library-specific
            return;
        }

        const { currentPlan } = await Api.trialDetail.get({ t: new Date().getTime() });
        if (currentPlan === "basic" || currentPlan === "edu") {
            // Not a pro user, tours are only shown for pro users
            return;
        }

        // The following code is to be executed only once, so mark the user as having taken the tour
        await setHasTakenTour();

        const presentationIds = this.user.getPresentationIds();
        const presentationsMetadata = await presentationsApi.getPresentationsMetadata({ presentationIds });
        const hasEditablePresentations = presentationsMetadata.filter(metadata => metadata.permissions.write && metadata.workspaceId === workspaceId).length > 0;
        const hasRecommendedTemplatePresentations = presentationsMetadata.filter(metadata => metadata.isFromRecommendedTemplate).length > 0;

        if (!hasEditablePresentations) {
            // Doesn't have any editable presentations, show the generate presentation tour so they can generate them
            _.delay(async () => {
                // @ts-ignore
                const { GeneratePresentationDialog } = await import(/* webpackMode: "eager" */"js/react/components/Dialogs/GeneratePresentationDialog");
                ShowDialog(GeneratePresentationDialog);
            }, 1000);

            return;
        }

        // Reporting the experiment group assignment, this will trigger an appcue flow if user is in the variant-a group
        // WARNING: Since Appcues is configured to show the flow for variant-a, we have to use the hasRecommendedTemplatePresentations flag which is more
        // strict than the actual experiment group assignment as experiment group assignment can be true (variant-a) for users who already had presentations
        // when they signed up and we didn't copy recommended templates to their workspace
        trackActivity("ExperimentGroup", "Assigned", hasRecommendedTemplatePresentations ? "variant-a" : "control", null, {
            experiment_id: "5FC18A79E182FE7C764425D4F852F501",
            experiment_group_assignment: hasRecommendedTemplatePresentations ? "variant-a" : "control"
        });
    }

    showLibrary({ filter }: { filter?: PresentationLibraryFilter } = {}) {
        let path = "/library";
        if (filter) {
            if (filter.type == "folder") {
                path += `/folder/${filter.folderId}`;
            } else if (filter.type == "team_folder") {
                path += `/team_folder/${filter.folderId}`;
                if (filter.subFolderId) {
                    path += `/${filter.subFolderId}`;
                }
            } else {
                path += "/" + filter.type;
            }
        }

        browserHistory.push(path);
    }

    isPersonalWorkspace() {
        return this.workspaceId === "personal";
    }

    isUserTeamMember() {
        const currentTeam = this.currentTeam;
        if (!currentTeam) {
            return false; // User is not in a team
        }
        const userRole = currentTeam.getUserRole(this.user.id);
        return userRole !== null && !isOwnerOrLibrarian(userRole);
    }

    showTeamResources({ pane }: { pane?: string } = {}) {
        browserHistory.push(`/team-resources/${pane || ""}`);
    }

    showMyResources({ pane }: { pane?: string } = {}) {
        browserHistory.push(`/my-resources/${pane || ""}`);
    }

    showAccountPane({ pane }: { pane?: string } = {}) {
        let url = `/account/${pane || ""}`;
        let presentationId = PresentationEditorController.presentation?.id;
        if (presentationId) {
            let slideIndex = PresentationEditorController.getCurrentSlideIndex() + 1;
            url += `?returnId=${presentationId}&slideIndex=${slideIndex}`;
        }
        browserHistory.push(url);
    }

    async showPresentationSettings(startPane = "share", presentation: typeof Presentation) {
        // @ts-ignore
        const { default: PresentationSettingsContainer } = await import("js/react/views/PresentationSettings/PresentationSettingsContainer");

        await ShowDialogAsync(PresentationSettingsContainer, {
            startPane,
            presentation
        });
    }

    showEditor({
        presentationId,
        slideIndex = 1,
        openInNewTab = false,
        withAppcue = null,
    }) {
        let url = `/${presentationId}/${slideIndex}`;

        if (withAppcue) {
            url = `${url}?with-appcue=${withAppcue}`;
        }

        if (openInNewTab) {
            window.open(url, "_blank");
        } else {
            browserHistory.push(url);
        }
    }

    showSlideEditor({ slideId, resourceType }) {
        if (slideId) {
            browserHistory.push(`/slide/${resourceType}/${slideId}`);
        } else {
            browserHistory.push(`/slide/${resourceType}`);
        }
    }

    goToRoute(path) {
        browserHistory.push(path);
    }

    showCreatePresentationDialog({ pane, state }) {
        browserHistory.push({
            pathname: `/create-presentation/${pane || ""}`,
            state
        });
    }

    async showThemeEditor(presentation: IStorageModel<IPresentation>) {
        // @ts-ignore
        const { default: ThemeEditor } = await import(/* webpackMode: "eager" */ "js/editor/ThemeEditor/ThemeEditor");
        return ShowDialogAsync(ThemeEditor, { presentation });
    }

    navigateToThemeEditor(themeOrPresentationId: string, returnPath = window.location.pathname) {
        browserHistory.push(`/theme/${themeOrPresentationId}?return=${encodeURIComponent(returnPath)}`);
    }

    playPresentation({ presentationId, slideIndex = 0 }, state = {}) {
        browserHistory.push(`/player/${presentationId}/${slideIndex + 1}`, state);
    }

    showDocumentEditor({ presentationId, openInNewTab }) {
        const url = `/document/${presentationId}`;

        if (openInNewTab) {
            window.open(url, "_blank");
        } else {
            browserHistory.push(url);
        }
    }

    async initializeUser(firebaseUser) {
        if (this.user) {
            // Dispose all existing subscriptions
            this.user.off();
        }

        await this._updateState({ firebaseUser });

        // create the user
        const user = new User({ id: this.firebaseUser.uid }, { autoLoad: false });

        // legacy
        app.user = user;

        // initialize datatservices
        await ds.setup();

        // load the User model
        await user.load();

        const isAccountVerified = this.firebaseUser.emailVerified;
        // check for email verification
        if (!isAccountVerified) {
            // wait for verification
            user.waitForVerifiedEmail().then(() => {
                this._updateState({ isAccountVerified: true });
            });
        }

        await this._updateState({
            user,
            isAccountVerified
        });

        // show dialog if the user has been downgraded since last login
        if (this.user.has("forcedDowngrade")) {
            ShowDialog(ForcedDowngradeDialog);
            if (this.user.has("forcedDowngrade")) {
                this.user.update({ forcedDowngrade: null });
            }
        }
        // subscribe to downgrade event so that at the end of the user's pro trial,
        // they get downgraded immediately
        this.user.on("change:forcedDowngrade", model => {
            if (model && model.changed && model.changed.forcedDowngrade === true) {
                ShowDialog(MandatoryUpdateDialog, {
                    message: "This browser tab will need to be refreshed since your account was downgraded."
                });
            }
        });

        const createForcedRemovalDialogOptions = (teamName: string) => ({
            title: (
                <Fragment>
                    You've been removed from
                    <span style={{ color: "#159bcd", fontWeight: 900 }}>{teamName}</span>
                </ Fragment >
            ),
            message: "You no longer have access to any presentations or shared resources within that workspace",
            buttonText: "Got It",
            buttonOptions: { color: "blue" },
            onClick: () => { }
        });

        if (this.user.has("forcedWorkspaceRemoval")) {
            const options = createForcedRemovalDialogOptions(this.user.get("forcedWorkspaceRemoval"));
            ShowMessageDialog(options);
            this.user.update({ forcedWorkspaceRemoval: null });
        }

        this.user.on("change:forcedWorkspaceRemoval", model => {
            if (model && model.changed && !!model.changed.forcedWorkspaceRemoval) {
                const options = createForcedRemovalDialogOptions(model.changed.forcedWorkspaceRemoval);
                options.onClick = () => window.location.reload();
                ShowMessageDialog(options);
                this.user.update({ forcedWorkspaceRemoval: null });
            }
        });

        this.user.on("change:workspaceDeleted", model => {
            if (model && model.changed && !!model.changed.workspaceDeleted) {
                ShowDialog(MandatoryUpdateDialog, {
                    message: `This browser tab will need to be refreshed since the ${model.changed.workspaceDeleted} workspace was deleted.`
                });
                this.user.update({ workspaceDeleted: null });
            }
        });
    }

    initializeNotificationsService() {
        if (this.notificationsService) {
            this.notificationsService.dispose();
        }

        this.notificationsService = new NotificationsService(this.user.get("id"));

        // Store the user's modifiedAt value before any modifications happen
        //   so we have a benchmark for the last time they were logged in
        const userModifiedAt = this.user.get("modifiedAt");
        // Subscribe to old notifications
        // This function gets called every time the `unread` array is changed. Since the array is always
        //   updated one item at a time, we debounce so we only process the complete result.
        const onUnreadLoaded = _.debounce(() => {
            // don't interrupt presentations
            if (this.view === AppView.PLAYER) {
                return;
            }

            // Notify the user of any unread team invites
            const unread = this.notificationsService.unread
                .filter(x => x.event.createdAt > userModifiedAt)
                .sort((a, b) => b.event.createdAt - a.event.createdAt);

            if (unread.length) {
                unread.forEach(notification => {
                    if (notification.eventType === "team" && notification.event.action === "invitedToTeam" && !app.isConstrained) {
                        ShowDialog(MemberAddedToOrgDialog, { notification });
                    }
                });
                // Update the user's `modifiedAt` field so we don't spam the user on next login
                this.user.update({ modifiedAt: Date.now() });
            }

            // Unsubscribe now that we've processed the loaded array of unread notifications
            this.notificationsService.offUnreadChanged(onUnreadLoaded);
        }, 500);
        this.notificationsService.onUnreadChanged(onUnreadLoaded);

        // Subscribe to new notifications
        this.notificationsService.onNewUnread(notification => {
            // Don't interrupt presentations
            if (this.view === AppView.PLAYER) {
                return;
            }
            // Ensuring backwards compatibility, assuming notifyWithPopup == true unless it's
            // explicitly set to false
            const notifyWithPopup = this.user.get("notifyWithPopup") === false ? false : true;
            if (notification.eventType === "team" && notification.event.action === "invitedToTeam" && !app.isConstrained) {
                ShowDialog(MemberAddedToOrgDialog, { notification });
            } else if (notifyWithPopup && !app.isConstrained) {
                ShowNotificationAlert(notification);
            }
        });
    }

    async setInitialWorkspaceId(initialWorkspaceId?: string) {
        let workspaceId: string = initialWorkspaceId;

        if (!workspaceId) {
            workspaceId = getInitialWorkspaceId(this.user.workspacesMetadata);
        }

        await this._updateState({ workspaceId });
    }

    _stateDidUpdate(prevState: AppControllerState) {
        const { workspaceId, user, firebaseUser } = this._state;

        if (prevState.workspaceId !== workspaceId || prevState.firebaseUser !== firebaseUser || prevState.user !== user) {
            if (!workspaceId || !firebaseUser || !user) {
                WorkspaceController.reset();
                return;
            }

            WorkspaceController.initialize(workspaceId, firebaseUser, user);
        }
    }

    async switchWorkspace(workspaceId: string, goToLibrary: boolean = true) {
        // Always show the library when switching workspaces unless specified otherwise
        if (goToLibrary) {
            this.showLibrary({ filter: { type: PresentationFilterType.RECENT, folderId: null, subFolderId: null } });
        }

        await this._updateState({ workspaceId });
    }

    async showDebugInfo() {
        // @ts-ignore
        const { DebugInfoDialog } = await import(/* webpackMode: "eager" */ "js/react/components/Dialogs/DebugInfoDialog");
        ShowDialog(DebugInfoDialog);
    }

    async showInternalBugReport() {
        // @ts-ignore
        const { InternalBugReportDialog } = await import(/* webpackMode: "eager" */ "js/react/components/Dialogs/InternalBugReportDialog");
        ShowDialog(InternalBugReportDialog);
    }
}

// this is for backwards compatibilty with all the code that references app.mainView.editorView
if (!app.mainView) {
    app.mainView = {
        editorView: null
    };
}

const appController = new AppController(initialState);

export default appController;

// DEV-START
if (!global.jestTestActive) {
    // @ts-ignore
    if (import.meta.webpackHot) {
        // @ts-ignore Force refresh
        import.meta.webpackHot.decline();
    }
}
// DEV-END
